import { type FC, Fragment, Suspense, useState } from "react";
import type { MetaAdsAdSetReportItem } from "shared";
import { Icon } from "../../common/Icon";
import { useMetaAdsAdSets } from "../useMetaAdsAdSets";
import { useMetaAdsStatusUpdate } from "../useMetaAdsStatusUpdate";
import { AdTable } from "./AdTable";
import { MetricsColumns } from "./MetricsColumns";
import { StatusToggle } from "./StatusToggle";

const AdSetTableInner: FC<{
	campaignId: string;
	from: string;
	to: string;
	showStatusToggle: boolean;
}> = ({ campaignId, from, to, showStatusToggle }) => {
	const { data: adSets } = useMetaAdsAdSets(campaignId, from, to);
	const { updateAdSetStatus } = useMetaAdsStatusUpdate();
	const [expandedAdSets, setExpandedAdSets] = useState<Set<string>>(new Set());

	const toggleAdSet = (id: string) => {
		setExpandedAdSets((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	if (adSets.length === 0) {
		return (
			<tr>
				<td colSpan={12} style={{ textAlign: "center" }} className="text-muted">
					No ad sets found
				</td>
			</tr>
		);
	}

	return (
		<>
			{adSets.map((adSet: MetaAdsAdSetReportItem) => (
				<Fragment key={adSet.objectId}>
					<tr
						style={{ cursor: "pointer", background: "#f8f9fa" }}
						onClick={() => toggleAdSet(adSet.objectId)}
					>
						<td style={{ paddingLeft: 32 }}>
							<Icon
								name={
									expandedAdSets.has(adSet.objectId)
										? "keyboard_arrow_down"
										: "chevron_right"
								}
								size={16}
							/>{" "}
							{adSet.name}
						</td>
						<td>
							<span
								className={`badge ${adSet.effectiveStatus === "ACTIVE" ? "bg-success" : adSet.effectiveStatus === "PAUSED" ? "bg-warning" : "bg-secondary"}`}
							>
								{adSet.effectiveStatus}
							</span>
							{showStatusToggle &&
								(adSet.effectiveStatus === "ACTIVE" ||
									adSet.effectiveStatus === "PAUSED") && (
									// biome-ignore lint/a11y/noStaticElementInteractions: propagation boundary only
									<span
										style={{ marginLeft: 8 }}
										onClick={(e) => e.stopPropagation()}
										onKeyDown={(e) => e.stopPropagation()}
									>
										<StatusToggle
											objectId={adSet.objectId}
											currentStatus={adSet.effectiveStatus}
											onToggle={(id, status) =>
												updateAdSetStatus.mutate({
													adSetId: id,
													status,
												})
											}
											isPending={updateAdSetStatus.isPending}
										/>
									</span>
								)}
						</td>
						{showStatusToggle && <td />}
						<MetricsColumns item={adSet} />
					</tr>
					{expandedAdSets.has(adSet.objectId) && (
						<AdTable
							adSetId={adSet.objectId}
							from={from}
							to={to}
							showStatusToggle={showStatusToggle}
						/>
					)}
				</Fragment>
			))}
		</>
	);
};

export const AdSetTable: FC<{
	campaignId: string;
	from: string;
	to: string;
	showStatusToggle: boolean;
}> = (props) => (
	<Suspense
		fallback={
			<tr>
				<td colSpan={12} style={{ textAlign: "center" }}>
					Loading ad sets...
				</td>
			</tr>
		}
	>
		<AdSetTableInner {...props} />
	</Suspense>
);
